import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the HiringRequestsContext
const HiringRequestsContext = createContext();

// Custom hook to use the context
export const useHiringRequests = () => useContext(HiringRequestsContext);

// Provider component
export const HiringRequestsProvider = ({ children }) => {
  const [requests, setRequests] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 15,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = "https://hrmspayroll.prepstripe.com/api/";
  const token = getToken();

  const axiosInstance = useMemo(
    () =>
      axios.create({
        baseURL: API_URL,
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
    [API_URL, token]
  );

  // Fetch all hiring requests
  const fetchHiringRequests = async (page = 1) => {
    try {
      setLoading(true);
      setError(null); // Reset error state
      const response = await axiosInstance.get(`recruitment-requests?page=${page}`);
      await new Promise((r) => setTimeout(r, 1000));
      // setRequests(response.data.data.data);
      setRequests(response.data?.data);
      setPagination({
        currentPage: response.data?.meta.current_page || 1,
        totalPages: response.data?.meta.last_page || 1,
        totalItems: response.data?.meta.total || 0,
        perPage: response.data?.meta.per_page || 10,
      });
    } catch (err) {
      console.error("Error fetching hiring requests:", err);
      setError(err.response?.data?.message || "Failed to fetch hiring requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHiringRequests();
  }, []);

  const addHiringRequest = async (newRequest) => {
    try {
      // For FormData, axios automatically sets Content-Type with boundary
      // We only need to ensure Authorization is set
      const config = newRequest instanceof FormData 
        ? {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            // Let axios automatically set Content-Type for FormData
          }
        : undefined; // Use default instance headers for JSON
      
      const response = await axiosInstance.post("recruitment-requests", newRequest, config);
      const addedRequest = response.data.data;
      
      // Handle file URLs from response
      if (response.data.data) {
        addedRequest.approval_attachment_mail_url = response.data.data.approval_attachment_mail_url;
        addedRequest.job_description_url = response.data.data.job_description_url;
        addedRequest.kra_kpi_url = response.data.data.kra_kpi_url;
        addedRequest.technical_test_path_url = response.data.data.technical_test_path_url;
      }
      
      // setRequests((prevRequests) => [...prevRequests, addedRequest]);
      setRequests((prevRequests) => [addedRequest, ...prevRequests]);
      return { success: true, message: response?.data.message }; 
    } catch (err) {
      console.error("Error adding hiring request:", err);
      setError(err.response?.data?.message || "Failed to add hiring request.");
      return { success: false, error: err?.response?.data.message };
    }
  };

  const updateHiringRequest = async (updatedRequest) => {
    try {
      // Handle FormData - extract ID from FormData
      let requestId;
      if (updatedRequest instanceof FormData) {
        requestId = updatedRequest.get('id');
      } else {
        requestId = updatedRequest.id;
      }
      
      // For FormData, axios automatically sets Content-Type with boundary
      // We only need to ensure Authorization is set
      const config = updatedRequest instanceof FormData 
        ? {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            // Let axios automatically set Content-Type for FormData
          }
        : undefined; // Use default instance headers for JSON
      
      const response = await axiosInstance.post(`recruitment-requests/${requestId}`, updatedRequest, config);
      const updatedData = response.data.data;
      
      // Handle file URLs from response
      if (response.data.data) {
        updatedData.approval_attachment_mail_url = response.data.data.approval_attachment_mail_url;
        updatedData.job_description_url = response.data.data.job_description_url;
        updatedData.kra_kpi_url = response.data.data.kra_kpi_url;
        updatedData.technical_test_path_url = response.data.data.technical_test_path_url;
      }
      
      setRequests((prevRequests) =>
        prevRequests.map((request) =>
          request.id === updatedData.id ? { ...request, ...updatedData } : request
        )
      );
      return { success: true, message: response?.data.message }; 
    } catch (err) {
      console.error("Error updating hiring request:", err);
      setError(err.response?.data?.message || "Failed to update hiring request.");
      return { success: false, error: err?.response?.data.message };
    }
  };

  const deleteHiringRequest = async (id) => {
    try {
      await axiosInstance.delete(`recruitment-requests/${id}`);
      setRequests((prevRequests) => prevRequests.filter((request) => request.id !== id));
    } catch (err) {
      console.error("Error deleting hiring request:", err);
      setError(err.response?.data?.message || "Failed to delete hiring request.");
    }
  };

  const value = useMemo(
    () => ({
      requests,
      loading,
      error,
      pagination,
      fetchHiringRequests,
      addHiringRequest,
      updateHiringRequest,
      deleteHiringRequest,
    }),
    [requests, loading, error]
  );

  return <HiringRequestsContext.Provider value={value}>{children}</HiringRequestsContext.Provider>;
};
